import React, { type FunctionComponent } from 'react'
import { css } from '@emotion/react'
import { borderRadius, colors, spacing, Text } from '@retailer-platform/shared-components'
import { MerchandisingFormSection } from 'domains/merchandising/components/form-section/MerchandisingFormSection'
// import { BannerDestinationValues } from 'domains/banners/types/banner.types'
import { type ContentManagementFlyoutPlacement, type ButtonVariantEnum } from 'domains/banners/api'
import { useDashMessages } from 'intl/intl.hooks'

interface FlyoutFields
  extends Pick<
    ContentManagementFlyoutPlacement,
    | 'titleText'
    | 'descriptionText'
    | 'disclaimerText'
    | 'primaryButton'
    | 'secondaryButton'
    | 'linkButton'
    | 'primaryDestination'
    | 'secondaryDestination'
    | 'linkDestination'
  > {}

type FieldProps = {
  name: string
  value?: string
  destinationType?: any
  destination?: any
  isDestination?: boolean
  styleVariant?: ButtonVariantEnum
}

const sectionContainer = css({
  display: 'flex',
  flexDirection: 'column',
})

const rowContainer = css({
  display: 'flex',
  marginBottom: spacing.X24,
  width: '100%',
})

const textContainer = css({
  width: '280px',
})

const colorBlockStyle = css({
  background: colors.GRAYSCALE.X70,
  borderRadius: borderRadius.X4,
  display: 'inline-block',
  height: '16px',
  marginRight: spacing.X8,
  width: '16px',
  verticalAlign: 'middle',
})

export const FlyoutDetailsContentText: FunctionComponent<FlyoutFields> = ({
  primaryButton,
  secondaryButton,
  linkButton,
  primaryDestination,
  secondaryDestination,
  linkDestination,
  titleText,
  descriptionText,
  disclaimerText,
}) => {
  const messages = useDashMessages({
    colorLabel: 'bannersDomain.details.color.label',
    backgroundColorLabel: 'bannersDomain.details.backgroundColor.label',
    destinationTypeLabel:
      'bannersDomain.banners.section.link-destination.field.destinationType.label',
    destinationLabel: 'bannersDomain.banners.section.link-destination.target',
    styleLabel: 'bannersDomain.banners.section.link-destination.style',
    emptyValue: 'bannersDomain.field.empty.value',
    imageLabel: 'bannersDomain.details.content.field.image.label',
  })

  const fields: FieldProps[] = [
    {
      name: 'Title',
      value: titleText.text || messages.emptyValue,
    },

    {
      name: 'Description',
      value: descriptionText.text || messages.emptyValue,
    },

    {
      name: 'Disclaimer',
      value: disclaimerText.text || messages.emptyValue,
    },
    {
      name: 'PRIMARY BUTTON',
      value: primaryButton.label || messages.emptyValue,
      destinationType: primaryDestination?.type,
      destination: primaryDestination?.displayValue,
      isDestination: true,
      styleVariant: primaryButton.styleVariant,
    },
    {
      name: 'SECONDARY BUTTON',
      value: secondaryButton.label || messages.emptyValue,
      destinationType: secondaryDestination?.type,
      destination: secondaryDestination?.displayValue,
      isDestination: true,
      styleVariant: secondaryButton.styleVariant,
    },
    {
      name: 'LINK BUTTON',
      value: linkButton.label || messages.emptyValue,
      destinationType: linkDestination?.type,
      destination: linkDestination?.displayValue,
      isDestination: true,
      styleVariant: linkButton.styleVariant,
    },
  ]

  return (
    <MerchandisingFormSection titleId="bannersDomain.details.content.title">
      <div css={sectionContainer}>
        {fields.map(
          ({ name, value, destination, destinationType, isDestination, styleVariant }) => (
            <div css={rowContainer} key={`banner-details-info-${name}`}>
              {value && (
                <>
                  <div css={textContainer}>
                    <Text weight="semibold">{name}</Text>
                    <Text size="medium">{value}</Text>
                  </div>
                  {isDestination && (
                    <>
                      <div css={textContainer}>
                        <Text weight="semibold">{messages.styleLabel}</Text>
                        {styleVariant && <Text size="medium">{styleVariant}</Text>}
                      </div>
                      <div css={textContainer}>
                        <Text weight="semibold">{messages.destinationTypeLabel}</Text>
                        {destinationType && <Text size="medium">{destinationType}</Text>}
                      </div>
                      <div css={textContainer}>
                        <Text weight="semibold">{messages.destinationLabel}</Text>
                        {destination && <Text size="medium">{destination}</Text>}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )
        )}
      </div>
    </MerchandisingFormSection>
  )
}
